import { Component } from '@theme/component'

const root = globalThis
const theme = root.theme || {}
root.theme = theme
theme.config = theme.config || {}
theme.utils = theme.utils || {}

theme.config.isTouch ??= 'ontouchstart' in root || navigator.maxTouchPoints > 0
theme.config.motionReduced ??=
  root.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
theme.config.mqlSmall ??=
  root.matchMedia?.('(max-width: 749px)')?.matches ?? false

theme.InitWhenVisible ??= class {
  constructor(callback) {
    if (typeof callback === 'function') {
      callback()
    }
  }
}

theme.utils.imageLoaded ??= (images) => {
  if (!images) {
    return Promise.resolve()
  }
  const elements = images instanceof Element ? [images] : Array.from(images)
  return Promise.all(
    elements.map(
      (image) =>
        new Promise((resolve) => {
          if (
            (image.tagName === 'IMG' && image.complete) ||
            image.tagName === 'VIDEO' ||
            image.tagName === 'IFRAME'
          ) {
            resolve()
            return
          }
          image.addEventListener('load', () => resolve(), {
            once: true
          })
          image.addEventListener('error', () => resolve(), {
            once: true
          })
        })
    )
  )
}

const MediaConfig = {
  markLoadedIdleTimeoutMs: 150
}

const requestIdle =
  root.requestIdleCallback ||
  function fallback(callback) {
    return setTimeout(callback, 1)
  }

class LazyImage extends HTMLImageElement {
  constructor() {
    super()
    this.handleLazy = this.handleLazy.bind(this)
    this.onLoad = this.onLoad.bind(this)
    this.onError = this.onError.bind(this)
  }

  connectedCallback() {
    if (!this.media) {
      return
    }

    root.addEventListener('resize', this.handleLazy, {
      passive: true
    })

    this.mutationObserver = new MutationObserver(() => {
      this.classList.remove('loaded')
      this.handleLazy()
    })

    this.mutationObserver.observe(this, {
      attributeFilter: ['src', 'srcset'],
      attributes: true
    })
    this.handleLazy()
  }

  disconnectedCallback() {
    root.removeEventListener('resize', this.handleLazy)
    this.mutationObserver?.disconnect()
  }

  get media() {
    this._media = this._media || this.closest('.media')
    return this._media
  }

  handleLazy() {
    if (this.classList.contains('loaded')) {
      return
    }
    this.media?.classList.add('loading')

    if (this.complete) {
      this.onLoad()
      return
    }

    this.addEventListener('load', this.onLoad, { once: true })
    this.addEventListener('error', this.onError, { once: true })
  }

  onLoad() {
    const markLoaded = () => {
      this.classList.add('loaded')
      this.media?.classList.remove('loading')
    }
    requestIdle(markLoaded, { timeout: MediaConfig.markLoadedIdleTimeoutMs })
  }

  onError() {
    this.media?.classList.remove('loading')
  }
}

class MediaElement extends Component {
  connectedCallback() {
    super.connectedCallback()
    // Parallax removido; reservado para comportamentos específicos do media
  }

  get media() {
    return Array.from(
      this.querySelectorAll(
        'picture>img, video, svg, video-media, deferred-media, g-map'
      )
    )
  }
}

if (!customElements.get('lazy-image')) {
  customElements.define('lazy-image', LazyImage, { extends: 'img' })
}
if (!customElements.get('media-element')) {
  customElements.define('media-element', MediaElement)
}
